import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { shouldShowUnreadIndicatorForItem } from "../Utils/chatUnread";

const ChatFriendList = ({
  chatItems,
  onSelectUser,
  activeConversation,
  handleAddClick,
  onDeleteConversation,
  showUnreadConversations,
  showUnread,
}) => {
  const location = useLocation();

  const [search, setSearch] = useState("");

  // Search filter
  const searchedChats = chatItems.filter((item) => {
    const searchTerm = search.toLowerCase();
    const nameMatch = item.name.toLowerCase().includes(searchTerm);
    const lastMessageMatch =
      item.lastMessage && item.lastMessage.toLowerCase().includes(searchTerm);
    return nameMatch || lastMessageMatch;
  });

  const isConversationUnread = (item) => !!item.isUnread;

  const shouldShowUnreadIndicator = (item) =>
    shouldShowUnreadIndicatorForItem(item, activeConversation);

  const filteredChats = showUnread
    ? searchedChats.filter((item) => isConversationUnread(item))
    : searchedChats;

  const decodeAndStripHtml = (text) => {
    if (!text) return "";
    // Recursively decode HTML entities up to 3 layers (handles double-encoded like &amp;#x27;)
    let decoded = text;
    let prev;
    let iterations = 0;
    do {
      prev = decoded;
      const doc = new DOMParser().parseFromString(decoded, "text/html");
      decoded = doc.body.textContent || "";
      iterations++;
    } while (decoded !== prev && iterations < 3);
    return decoded;
  };

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const formatTime = (time) => {
    if (!time) return "";

    try {
      if (typeof time === "object" && time.seconds) {
        time = new Date(time.seconds * 1000);
      } else {
        time = new Date(time);
      }

      if (isNaN(time)) return "";

      return time.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return "";
    }
  };

  return (
    <div className="chat-sidebar active-option">
      <div className="chats-search">
        <input
          className="header-search-1 text-caption-c2"
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="d-flex gap-2 gap-md-3 text-body-b4 chat-btn-container">
        <NavLink
          to="/chat"
          className={({ isActive }) =>
            `btn-1 all-btn btn-1-basic chat-btn-1-basic text-decoration-none ${
              isActive ? "active" : ""
            }`
          }
          style={{ height: "30px" }}
        >
          All
        </NavLink>
        <NavLink
          to="/threads"
          className={({ isActive }) =>
            `btn-1 btn-1-basic chat-btn-1-basic text-decoration-none ${
              isActive ? "active" : ""
            }`
          }
          style={{ width: "81px", height: "30px" }}
        >
          Threads
        </NavLink>
        <NavLink
          to="/squads"
          className={({ isActive }) =>
            `btn-1 btn-1-basic chat-btn-1-basic text-decoration-none ${
              isActive ? "active" : ""
            }`
          }
          style={{ width: "81px", height: "30px" }}
        >
          Squads
        </NavLink>
        {(location.pathname === "/chat" || location.pathname === "/squads") && (
          <button className="chats-plus-btn" onClick={handleAddClick}>
            <img src="/icons/plus-icon.svg" alt="" />
          </button>
        )}
      </div>
      <div
        className="no-scrollbar flex-grow-1"
        style={{ height: "100%", minHeight: "0px", overflowY: "auto" }}
      >
        <button
          className="btn btn-sm btn-link text-decoration-none"
          style={{
            border: "1px solid #e6ecf1",
            color: showUnread ? "white" : "black",
            background: showUnread ? "#008cff" : "white",
          }}
          onClick={showUnreadConversations}
        >
          Unread Chats
        </button>
        {filteredChats.map((item, index) => {
          const isActive = activeConversation === item.conversationId;
          const showUnreadIndicator = shouldShowUnreadIndicator(item);
          const isUnread = isConversationUnread(item);
          const isOnline = item.isOnline;
          let lastMessageText = item.lastMessage || "No messages yet";
          lastMessageText = item.isUserLastMessage
            ? `You: ${lastMessageText}`
            : `${lastMessageText}`;
          if (item.type === "group" && lastMessageText !== "No messages yet") {
            lastMessageText = item.isUserLastMessage
              ? `You: ${lastMessageText}`
              : `${item.otherSenderName}: ${lastMessageText}`;
          }

          // Strip HTML tags and decode entities (handles &amp;#x27; → ')
          lastMessageText = decodeAndStripHtml(lastMessageText);

          if (lastMessageText.length > 30) {
            lastMessageText = lastMessageText.substring(0, 30) + "...";
          }

          return (
            <>
              <div
                key={index}
                className="d-flex align-items-center chat-parent"
                style={{ position: "relative" }}
              >
                <div
                  key={index}
                  className={`chat-item ${isActive ? "active" : ""} ${
                    isUnread ? "unread" : ""
                  }`}
                  onClick={() => {
                    onSelectUser(
                      item.conversationId,
                      item.type,
                      item.profileImage,
                      item.name,
                      item.members,
                    );
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex gap-2 gap-md-3">
                      <div className="position-relative">
                        {!item.isGroup ? (
                          <img
                            src={item.profileImage || "/icons/james.svg"}
                            alt="img"
                            className="profile-img"
                          />
                        ) : (
                          <img
                            src="/icons/group-user.svg"
                            alt="img"
                            className="profile-img"
                          />
                        )}
                        {showUnreadIndicator && (
                          <span className="unread-dot"></span>
                        )}
                        {!item.isGroup && (
                          <span
                            className={`online-status-dot ${
                              isOnline ? "online" : "offline"
                            }`}
                            title={isOnline ? "Online" : "Offline"}
                          ></span>
                        )}
                      </div>

                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`text-body-b1 ${
                              showUnreadIndicator ? "font-weight-bold" : ""
                            }`}
                          >
                            {item.name}
                          </span>
                          {showUnreadIndicator && (
                            <span className="unread-count-badge">
                              {item.unreadCount || 0}
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-body-b4 mb-0 text-truncate ${
                            showUnreadIndicator ? "font-weight-bold" : ""
                          }`}
                          title={lastMessageText}
                          style={{ maxWidth: "150px" }}
                        >
                          {lastMessageText}
                        </p>
                      </div>
                    </div>

                    <div className="d-flex flex-column align-items-end ms-auto">
                      <span
                        className={`text-small-s2 ${
                          showUnreadIndicator ? "font-weight-bold" : ""
                        }`}
                      >
                        {formatTime(item.lastMessageTime)}
                      </span>
                    </div>
                  </div>
                </div>
                <img
                  className="action-icon delete-icon"
                  src="/icons/delete-icon.svg"
                  onClick={() => onDeleteConversation(item.conversationId)}
                  alt="Delete"
                  title="Delete"
                />
              </div>
            </>
          );
        })}
      </div>
    </div>
  );
};

export default ChatFriendList;
